/**
 * Commentaires des visiteurs sur l'organisation et ses lieux (publiés ou en modération).
 * La modération reste à notre équipe ; ici, on lit et on va voir la fiche.
 */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { fetchInsights } from '../api'
import { TypeIcon } from '../components/badges'
import { useOrg } from '../utils/orgContext'
import { formatDate } from '../utils/format'
import { publicLocationUrl } from '../utils/links'

export function AvisPage() {
  const { t } = useTranslation('gestion')
  const { orgId } = useOrg()
  const [element, setElement] = useState('all')
  const [status, setStatus] = useState<'all' | 'approved' | 'pending'>('all')
  const { data, isLoading, error } = useQuery({
    queryKey: ['insights', orgId, 365, 'comments'],
    queryFn: ({ signal }) => fetchInsights(orgId, 365, signal, 200),
  })

  const elements = useMemo(
    () => (data?.elements ?? []).filter((e) => e.comment_count > 0).sort((a, b) => b.comment_count - a.comment_count),
    [data],
  )
  const comments = (data?.recent_comments ?? []).filter(
    (c) => (element === 'all' || `${c.location_type}-${c.location_id}` === element) && (status === 'all' || c.status === status),
  )

  return (
    <div className="g-page">
      <div className="g-page__head">
        <div>
          <h1 className="g-page__title">{t('avis.titre')}</h1>
          <p className="g-muted">{t('avis.intro')}</p>
        </div>
      </div>

      <div className="g-toolbar">
        <select className="g-input g-select" value={element} onChange={(e) => setElement(e.target.value)} aria-label={t('avis.element')}>
          <option value="all">{t('avis.tous_les_elements')}</option>
          {elements.map((e) => (
            <option key={`${e.type}-${e.id}`} value={`${e.type}-${e.id}`}>
              {e.name} ({e.comment_count})
            </option>
          ))}
        </select>
        <div className="g-chips" role="group">
          {(['all', 'approved', 'pending'] as const).map((s) => (
            <button key={s} type="button" className={`g-chip${status === s ? ' g-chip--active' : ''}`} onClick={() => setStatus(s)} aria-pressed={status === s}>
              {t(`avis.statut_${s}`)}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="g-alert g-alert--error">{(error as Error).message}</p>}
      {isLoading && <p className="g-muted">{t('commun.chargement')}</p>}
      {!isLoading && comments.length === 0 && (
        <div className="g-empty">
          <span className="mdi mdi-comment-off-outline g-empty__icon" aria-hidden />
          <p>{t('avis.aucun')}</p>
        </div>
      )}

      <ul className="g-comments g-comments--page">
        {comments.map((c) => (
          <li key={c.id} className="g-card g-comment">
            <div className="g-comment__meta">
              <span className="g-user__avatar g-user__avatar--sm" aria-hidden>
                {c.author.slice(0, 1).toUpperCase()}
              </span>
              <strong>{c.author}</strong>
              <span className="g-muted">· {formatDate(c.created_at)}</span>
              {c.status === 'pending' && <span className="g-badge g-badge--warn">{t('avis.en_moderation')}</span>}
              <span className="g-spacer" />
              <a href={publicLocationUrl({ id: c.location_id, type: c.location_type })} target="_blank" rel="noreferrer" className="g-link g-comment__place">
                <TypeIcon type={c.location_type} /> {c.location_name}
              </a>
            </div>
            <p className="g-comment__body">{c.body}</p>
          </li>
        ))}
      </ul>
      <p className="g-hint">{t('avis.note_moderation')}</p>
    </div>
  )
}
